import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, map, tap } from 'rxjs';

import { SKIP_GLOBAL_ERROR } from '../http-context';
import { environment } from '../../environments/environment';
import { GlobalResponse } from '../models/global-response';
import { BooleanResponse } from '../models/boolean-response';
import { ProfileDTO, UpdateProfileRequest } from '../models/profile-dto';
import { AuthService, LoginResponse } from './auth.service';

@Injectable({ providedIn: 'root' })
export class ProfileService {

  private readonly baseUrl = `${environment.apiUrl}`;
  private readonly http = inject(HttpClient);
  readonly #auth = inject(AuthService);

  /** The signed-in account, shared by the header and the profile screen so
   *  a rename reaches both without a second round trip. */
  readonly profile = signal<ProfileDTO | null>(null);

  /** Replacing a photo does not change its URL, so an <img> already showing
   *  the old blob keeps showing it — the header did exactly that while the
   *  profile card updated. Every avatar in the app reads this instead. */
  private readonly avatarVersion = signal(0);

  readonly avatarSrc = computed(() => {
    const url = this.profile()?.avatarUrl;
    return url ? `${url}?v=${this.avatarVersion()}` : null;
  });

  avatarChanged(): void {
    this.avatarVersion.update(v => v + 1);
  }

  /** Every caller renders its own failure — the header silently, the form
   *  inline — so none of them want the generic modal on top. */
  private readonly quiet = {
    context: new HttpContext().set(SKIP_GLOBAL_ERROR, true)
  };

  /** Fetch once per ACCOUNT; the header calls this on every page. Keyed on
   *  the token's subject, because signing out and in again never reloads
   *  this tab: a plain "fetch once" cache kept showing the previous
   *  account's name, email and id to whoever signed in next. */
  load(): void {
    const account = this.#auth.accountId();
    const cached = this.profile();
    if (cached && String(cached.id) === account) return;
    if (cached) this.profile.set(null);
    this.refresh().subscribe({ error: () => undefined });
  }

  refresh(): Observable<ProfileDTO> {
    return this.http
      .get<GlobalResponse<ProfileDTO>>(
        `${this.baseUrl}/admin/users/me`, this.quiet
      )
      .pipe(map(res => res.result), tap(p => this.profile.set(p)));
  }

  update(body: UpdateProfileRequest) {
    return this.http.put<GlobalResponse<BooleanResponse>>(
      `${this.baseUrl}/admin/users/me`, body, this.quiet
    );
  }

  /** Password, avatar: the app's own self-service endpoints. They are
   *  gated on the session alone, so a CMS token is all they need.
   *
   *  Changing the password revokes every existing session server-side, so
   *  the response returns a fresh token pair the caller must adopt or the
   *  next request 401s. */
  changePassword(currentPassword: string, newPassword: string) {
    return this.http.put<GlobalResponse<LoginResponse>>(
      `${this.baseUrl}/user/change-password`,
      { currentPassword, newPassword },
      this.quiet
    );
  }

  uploadAvatar(file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.http.put<GlobalResponse<{ avatarUrl: string }>>(
      `${this.baseUrl}/user/upload-avatar`, form, this.quiet
    );
  }

  removeAvatar() {
    return this.http.put<GlobalResponse<BooleanResponse>>(
      `${this.baseUrl}/user/remove-avatar`, {}, this.quiet
    );
  }
}
